import { isNativeApp, normalizeReactRoute } from './nativeApp';

export const AVAILABLE_JOB_BROWSER_STATE_KEY = '__tempstarsAvailableJob';

type MemoryLocation = {
  pathname?: string;
};

type MemoryHistory = {
  location: MemoryLocation;
  listen: (listener: (location: MemoryLocation, action: string) => void) => () => void;
  goBack: () => void;
  goForward: () => void;
};

type BrowserHistory = {
  state: any;
  pushState: (data: any, unused: string, url?: string | URL | null) => void;
  back: () => void;
};

type BrowserEventTarget = {
  addEventListener: (type: 'popstate', listener: (event: PopStateEvent) => void) => void;
  removeEventListener: (type: 'popstate', listener: (event: PopStateEvent) => void) => void;
};

/**
 * Bridge only the Available Shifts list -> Shift Details transition into the
 * real browser history. The rest of the hybrid app stays on MemoryRouter, and
 * native Cordova history remains untouched.
 */
export function installAvailableJobsBrowserHistory({
  memoryHistory,
  browserHistory,
  browserEvents,
  currentUrl,
  native = isNativeApp(),
}: {
  memoryHistory: MemoryHistory;
  browserHistory: BrowserHistory;
  browserEvents: BrowserEventTarget;
  currentUrl: () => string;
  native?: boolean;
}): () => void {
  if (native) return () => {};

  let currentMemoryLocation = memoryHistory.location;
  let ignoreNextMemoryPop = false;
  let ignoreNextBrowserPop = false;

  const unlisten = memoryHistory.listen((nextLocation, action) => {
    const previousRoute = normalizeReactRoute(currentMemoryLocation.pathname);
    const nextRoute = normalizeReactRoute(nextLocation.pathname);
    currentMemoryLocation = nextLocation;

    // Browser-originated Back and Forward both produce a MemoryRouter POP.
    // Consume exactly that matching POP before evaluating in-app navigation;
    // otherwise Forward leaves this flag armed and suppresses the user's next
    // in-app Back action.
    if (action === 'POP' && ignoreNextMemoryPop) {
      ignoreNextMemoryPop = false;
      return;
    }

    if (action === 'PUSH' && previousRoute === 'availableJobs' && nextRoute === 'availableJob') {
      const existingState = browserHistory.state && typeof browserHistory.state === 'object'
        ? browserHistory.state
        : {};
      browserHistory.pushState({
        ...existingState,
        [AVAILABLE_JOB_BROWSER_STATE_KEY]: true,
      }, '', currentUrl());
      return;
    }

    if (action === 'POP' && previousRoute === 'availableJob' && nextRoute === 'availableJobs') {
      if (browserHistory.state?.[AVAILABLE_JOB_BROWSER_STATE_KEY]) {
        ignoreNextBrowserPop = true;
        browserHistory.back();
      }
    }
  });

  const onBrowserPop = (event: PopStateEvent) => {
    if (ignoreNextBrowserPop) {
      ignoreNextBrowserPop = false;
      return;
    }

    const currentRoute = normalizeReactRoute(currentMemoryLocation.pathname);
    const browserIsOnDetailsEntry = Boolean(event.state?.[AVAILABLE_JOB_BROWSER_STATE_KEY]);

    if (currentRoute === 'availableJob' && !browserIsOnDetailsEntry) {
      ignoreNextMemoryPop = true;
      memoryHistory.goBack();
      return;
    }

    if (currentRoute === 'availableJobs' && browserIsOnDetailsEntry) {
      ignoreNextMemoryPop = true;
      memoryHistory.goForward();
    }
  };

  browserEvents.addEventListener('popstate', onBrowserPop);

  return () => {
    unlisten();
    browserEvents.removeEventListener('popstate', onBrowserPop);
  };
}
